import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar3, Clock, ExclamationTriangle, JournalText, Search } from "react-bootstrap-icons";
import PageHero from "../../components/public/PageHero";
import Seo from "../../components/public/Seo";
import { BlogImage } from "../../components/landing/BlogSection";
import { listBlogs } from "./blogApi";

const PAGE_SIZE = 9;

const Card = ({ blog }) => (
  <article className="lp-card lp-card--lift h-100 d-flex flex-column">
    <Link to={`/blogs/${blog.slug}`} state={{ id: blog.id }} className="lp-blog-featured__link" aria-label={`Read ${blog.title}`}>
      <BlogImage blog={blog} />
      <div className="p-4 d-flex flex-column flex-grow-1">
        <p className="lp-blog__meta">
          {blog.date && <span><Calendar3 aria-hidden="true" /> {blog.date}</span>}
          <span><Clock aria-hidden="true" /> {blog.readTime}</span>
        </p>
        <h2 className="lp-blog__title lp-clamp-2">{blog.title}</h2>
        {blog.excerpt && <p className="lp-muted lp-clamp-3">{blog.excerpt}</p>}
        <span className="lp-link mt-auto">Read Article <ArrowRight aria-hidden="true" /></span>
      </div>
    </Link>
  </article>
);

/** /blogs - article list with search, category chips and "Load more" (public API POST /all/blogs). */
const BlogListPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [loadingMore, setLoadingMore] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const isFirst = page === 1;
    if (isFirst) setStatus("loading");
    else setLoadingMore(true);

    listBlogs({ page, size: PAGE_SIZE, keyword: query })
      .then((res) => {
        if (cancelled) return;
        setBlogs((prev) => (isFirst ? res.blogs : [...prev, ...res.blogs]));
        setTotalPages(res.totalPages);
        setStatus("success");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      })
      .finally(() => {
        if (!cancelled) setLoadingMore(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, query, attempt]);

  const categories = useMemo(() => ["All", ...Array.from(new Set(blogs.map((b) => b.category)))], [blogs]);
  const visible = category === "All" ? blogs : blogs.filter((b) => b.category === category);

  const submitSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setCategory("All");
    setQuery(keyword.trim());
  };

  return (
    <>
      <Seo
        title="Blog - Real Estate Guides & Insights | All Home Desire"
        description="Guides, market insights and tips for buying, selling and renting property in India."
        path="/blogs"
      />
      <PageHero title="Knowledge Hub" subtitle="Guides and insights for buyers, sellers and renters." crumbs={[{ label: "Blog" }]} />

      <section className="lp-section">
        <div className="container">
          <div className="d-flex flex-column flex-lg-row gap-3 justify-content-between mb-4">
            <form className="d-flex gap-2 flex-grow-1" role="search" onSubmit={submitSearch} style={{ maxWidth: 520 }}>
              <label htmlFor="blog-search" className="visually-hidden">Search articles</label>
              <input id="blog-search" type="search" className="lp-input" placeholder="Search articles" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
              <button type="submit" className="lp-btn lp-btn--primary" aria-label="Search"><Search aria-hidden="true" /></button>
            </form>
            {categories.length > 2 && (
              <div className="lp-filter-chips" role="group" aria-label="Filter by category">
                {categories.map((c) => (
                  <button key={c} type="button" className={`lp-pill${category === c ? " is-active" : ""}`} aria-pressed={category === c} onClick={() => setCategory(c)}>
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>

          {status === "loading" && (
            <div className="row g-4" role="status" aria-label="Loading articles">
              {[0, 1, 2].map((i) => (
                <div key={i} className="col-12 col-md-6 col-lg-4">
                  <div className="lp-card" aria-hidden="true">
                    <div className="lp-skeleton lp-blog__media" />
                    <div className="p-4">
                      <div className="lp-skeleton lp-skeleton--line w-50" />
                      <div className="lp-skeleton lp-skeleton--line lp-skeleton--lg w-100" />
                      <div className="lp-skeleton lp-skeleton--line w-75" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {status === "error" && (
            <div className="lp-empty" role="alert">
              <ExclamationTriangle aria-hidden="true" />
              <p>We couldn&apos;t load the articles right now.</p>
              <button type="button" className="lp-btn lp-btn--outline-dark" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
            </div>
          )}

          {status === "success" && visible.length === 0 && (
            <div className="lp-empty">
              <JournalText aria-hidden="true" />
              <p className="mb-0">{query ? "No articles match your search." : "No articles yet. Check back soon."}</p>
            </div>
          )}

          {status === "success" && visible.length > 0 && (
            <>
              <div className="row g-4">
                {visible.map((blog) => (
                  <div key={blog.key} className="col-12 col-md-6 col-lg-4"><Card blog={blog} /></div>
                ))}
              </div>
              {page < totalPages && (
                <div className="text-center mt-5">
                  <button type="button" className="lp-btn lp-btn--outline-dark" onClick={() => setPage((p) => p + 1)} disabled={loadingMore}>
                    {loadingMore ? "Loading..." : "Load more"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default BlogListPage;
